import React, { useState, useEffect } from 'react';
import { Empty } from 'rizzui';
import AppButton from '../AppButton/AppButton';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MongoosePagination from '../Pagination/MongoosePagination';
import CustomModal from '../CustomModal/CustumModal';

// Utility function for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

function AppTable({
  TH = [],
  TD = [],
  onRowClick,
  emptyMessage = "No data available",
  navigateTo,
  buttonText,
  modalComap,
  hidePagination,
  TableSearch,
  enableExport = false,
  onExportData,
  modalSize,
  onModalClose
}) {
  const TableDataReducer = useSelector((state) => state.TableReducer);
  const currentPage = TableDataReducer.pagination?.page || 1;
  const limit = TableDataReducer.pagination?.limit || 10;

  const normalizedTD = Array.isArray(TD) ? TD : [];

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth < 1024 && window.innerWidth >= 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const baseIndex = (currentPage - 1) * limit;

  const getVisibleColumns = () => {
    if (isMobile) return TH.filter(header => (header.priority || 1) <= 2);
    if (isTablet) return TH.filter(header => (header.priority || 1) <= 3);
    return TH;
  };

  const visibleColumns = getVisibleColumns();

  const handleExport = (format) => {
    setIsExportMenuOpen(false);
    if (onExportData) {
      onExportData(format);
    }
  };

  const MobileCardView = () => (
    <div className="space-y-3">
      {normalizedTD.map((row, rowIndex) => {
        const serialNumber = baseIndex + rowIndex + 1;

        return (
          <div
            key={`card-${row.id || rowIndex}`}
            className={cn(
              "bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200",
              onRowClick && "cursor-pointer active:scale-98 hover:shadow-md"
            )}
            onClick={() => onRowClick && onRowClick(row, rowIndex)}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 border-b border-gray-100 flex items-center justify-between">
              <div className="text-xs font-semibold text-gray-600">Record #{serialNumber}</div>
              {/* Show first important column in header if exists */}
              {TH[0] && TH[0].key !== 'serial' && (
                <div className="text-xs text-gray-500">
                  {typeof TH[0].render === 'function' && row[TH[0].key]
                    ? TH[0].render(row[TH[0].key], row, rowIndex)
                    : row[TH[0].key]
                  }
                </div>
              )}
            </div>

            {/* Card Body */}
            <div className="p-4">
              <div className="space-y-3">
                {TH.map((header, colIndex) => {
                  const key = header.key || header.title.toLowerCase().replace(/\s/g, '_');
                  // Skip serial number and first column (already shown in header)
                  if (key === 'serial' || colIndex === 0) return null;
                  const cellValue = row[key] !== undefined ? row[key] : '';

                  return (
                    <div key={`mobile-cell-${colIndex}`} className="flex items-start justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide flex-shrink-0 min-w-[100px]">
                        {header.title}
                      </div>
                      <div className="flex-1 text-right">
                        {typeof header.render === 'function'
                          ? header.render(cellValue, row, rowIndex)
                          : <div className="text-sm font-medium text-gray-900">{cellValue || 'N/A'}</div>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {(TableSearch || buttonText || enableExport) && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 py-3">
          <div className="flex flex-col sm:flex-row justify-between w-full items-stretch sm:items-center gap-3">
            <div className="flex-1">
              {TableSearch ?? <div />}
            </div>
            <div className="flex items-center gap-2 justify-end w-full sm:w-auto">
              {enableExport && (
                <div className="relative w-full sm:w-auto">
                  <button
                    onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors active:scale-95"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Data
                    <svg className={`w-4 h-4 transition-transform ${isExportMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isExportMenuOpen && (
                    <div className="absolute right-0 mt-2 w-full sm:w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <button
                        onClick={() => handleExport('csv')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export as CSV
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Export as PDF
                      </button>
                    </div>
                  )}
                </div>
              )}
              {buttonText && (
                <>
                  {navigateTo ? (
                    <NavLink to={navigateTo}>
                      <AppButton buttontext={buttonText} />
                    </NavLink>
                  ) : modalComap ? (
                    <AppButton
                      buttontext={buttonText}
                      onClick={() => setIsModalOpen(true)}
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {normalizedTD.length === 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 text-center">
            <Empty text={emptyMessage} />
          </div>
        </div>
      ) : (
        <>
          <div className="block sm:hidden">
            <MobileCardView />
          </div>

          <div className="hidden sm:block">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Table Header */}
                  <thead className="bg-gray-50">
                    <tr>
                      {visibleColumns.map((header, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={cn(
                            "px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider",
                            header.align === 'center' && "text-center",
                            header.align === 'right' && "text-right"
                          )}
                          style={header.width ? { width: header.width } : {}}
                        >
                          {header.title}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {normalizedTD.map((row, rowIndex) => {
                      const serialNumber = baseIndex + rowIndex + 1;

                      return (
                        <tr
                          key={`row-${row._id || rowIndex}`}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {visibleColumns.map((header, colIndex) => {
                            const key = header.key || header.title.toLowerCase().replace(/\s/g, '_');
                            const cellValue = row[key] ?? '';

                            if (key === 'serialNumber') {
                              return (
                                <td
                                  key={`cell-${colIndex}`}
                                  className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                                >
                                  {serialNumber}
                                </td>
                              );
                            }

                            return (
                              <td
                                key={`cell-${colIndex}`}
                                className={cn(
                                  "px-6 py-4 whitespace-nowrap text-sm text-gray-700",
                                  header.align === 'center' && "text-center",
                                  header.align === 'right' && "text-right"
                                )}
                              >
                                {typeof header.render === 'function'
                                  ? header.render(cellValue, row, rowIndex)
                                  : cellValue}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-center sm:justify-end mt-4">
        {!hidePagination && <MongoosePagination />}
      </div>

      {/* Modal for Add/Edit */}
      {modalComap && (
        <CustomModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            if (onModalClose) {
              onModalClose();
            }
          }}
          size={modalSize ||"xl"}
        >
          {typeof modalComap === 'function'
            ? modalComap(() => {
                setIsModalOpen(false);
                if (onModalClose) {
                  onModalClose();
                }
              })
            : React.cloneElement(modalComap, {
                onClose: () => {
                  setIsModalOpen(false);
                  if (onModalClose) {
                    onModalClose();
                  }
                }
              })
          }
        </CustomModal>
      )}
    </div>
  );
}

export default AppTable;
